import React, { useEffect, useRef } from 'react';
interface OtpInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  length?: number;
  autoFocus?: boolean;
  ariaLabel?: string;
}
/**
 * Standardized 6-box (configurable) numeric one-time-code input.
 * - Auto-advance on input, backspace navigation, paste support.
 * - First empty box gets a yellow highlight; filled boxes get a bold border.
 * - Each input has min-h 44 (touch) and a strong WCAG focus ring.
 */
export function OtpInput({
  value,
  onChange,
  length = 6,
  autoFocus,
  ariaLabel = 'One-time code'
}: OtpInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);
  const setDigit = (index: number, digit: string) => {
    if (digit && !/^\d$/.test(digit)) return;
    const next = [...value];
    next[index] = digit;
    onChange(next);
    if (digit && index < length - 1) refs.current[index + 1]?.focus();
  };
  const handleKey = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      refs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      refs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      refs.current[index + 1]?.focus();
    }
  };
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length).split('');
    if (pasted.length === 0) return;
    const next = Array(length).fill('') as string[];
    pasted.forEach((c, i) => next[i] = c);
    onChange(next);
    refs.current[Math.min(pasted.length, length - 1)]?.focus();
  };
  return <div role="group" aria-label={ariaLabel} className="flex justify-between gap-2" data-id="element-999">
      {Array.from({
      length
    }).map((_, index) => {
      const digit = value[index] ?? '';
      const isFilled = digit !== '';
      const isFirstEmpty = !isFilled && value.findIndex(d => d === '') === index;
      return <input key={index} ref={el => refs.current[index] = el} type="text" inputMode="numeric" autoComplete="one-time-code" aria-label={`Digit ${index + 1}`} maxLength={1} value={digit} onChange={e => setDigit(index, e.target.value)} onKeyDown={e => handleKey(index, e)} onPaste={handlePaste} className={`flex-1 min-w-0 h-14 max-w-[3.5rem] text-center text-2xl font-bold rounded-lg outline-none transition-all
              ${isFilled ? 'border-2 border-gray-900 bg-white text-gray-900' : isFirstEmpty ? 'border-2 border-[#FFCC00] bg-[#FFCC00]/5' : 'border-2 border-gray-500 bg-white'}
              focus:border-[#B8860B] focus:ring-2 focus:ring-[#B8860B] focus:ring-offset-1`} data-id="element-1000" />;
    })}
    </div>;
}